import { Component } from '@angular/core';
import { NgForm } from '@angular/forms';
import { PoBreadcrumb, PoDialogService, PoNotificationService } from '@po-ui/ng-components';
import { BalanceteService } from './balancete.service';

@Component({
  selector: 'app-balancete',
  templateUrl: './balancete.component.html',
  styleUrls: ['./balancete.component.css'],
  standalone: false
})
export class BalanceteComponent {

  contaInicial: number | null = null;
  contaFinal: number | null = null;
  periodo: { start?: string; end?: string } = {};
  empresaId: number | null = null;
  arquivoNome = '';
  arquivoPlanilha: File | null = null;
  isLoading = false;

  readonly breadcrumb: PoBreadcrumb = {
    items: [
      { label: 'Home', link: '/home' },
      { label: 'Contabilidade' },
      { label: 'Balancete - Conferencia Saldo' }
    ]
  };

  constructor(
    private readonly balanceteService: BalanceteService,
    private readonly poDialogService: PoDialogService,
    private readonly poNotificationService: PoNotificationService
  ) {}

  requestBalancete(form: NgForm): void {
    if (!form.valid) {
      this.poNotificationService.warning('Preencha corretamente todos os campos obrigatórios.');
      return;
    }

    if (!this.periodo?.start || !this.periodo?.end) {
      this.poNotificationService.warning('Informe o período desejado.');
      return;
    }

    if (this.contaInicial == null || this.contaFinal == null) {
      this.poNotificationService.warning('Informe conta inicial e conta final.');
      return;
    }

    if (this.arquivoPlanilha) {
      this.enviarPorPlanilha();
      return;
    }

    this.enviarPorEmpresa();
  }

  onArquivoSelecionado(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.processarArquivo(file);
  }

  onDropArquivo(event: DragEvent): void {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    this.processarArquivo(file);
  }

  onDragOverArquivo(event: DragEvent): void {
    event.preventDefault();
  }

  limparArquivo(): void {
    this.arquivoNome = '';
    this.arquivoPlanilha = null;
  }

  onEmpresaChange(): void {
    if (this.empresaId != null && this.empresaId > 0) {
      this.limparArquivo();
    }
  }

  private processarArquivo(file: File): void {
    const extensaoValida = /\.(csv|xlsx|xls)$/i.test(file.name);
    if (!extensaoValida) {
      this.poNotificationService.warning('Envie uma planilha .csv, .xlsx ou .xls.');
      return;
    }

    this.arquivoPlanilha = file;
    this.arquivoNome = file.name;
    this.empresaId = null;
    this.poNotificationService.success(
      'Planilha carregada. Clique em "Gerar Balancete" para enviar o grupo de empresas.'
    );
  }

  private enviarPorPlanilha(): void {
    if (!this.arquivoPlanilha || this.contaInicial == null || this.contaFinal == null) return;

    this.isLoading = true;
    this.balanceteService
      .requestBalancetePorPlanilha(
        this.arquivoPlanilha,
        this.periodo.start!,
        this.periodo.end!,
        this.contaInicial,
        this.contaFinal
      )
      .subscribe({
        next: () => this.tratarSucesso(true),
        error: (err) => this.tratarErro(err)
      });
  }

  private enviarPorEmpresa(): void {
    if (this.contaInicial == null || this.contaFinal == null) return;

    this.isLoading = true;
    this.balanceteService
      .requestBalancete({
        dataInicio: this.periodo.start!,
        dataFim: this.periodo.end!,
        contaInicial: this.contaInicial,
        contaFinal: this.contaFinal,
        empresaId: this.empresaId
      })
      .subscribe({
        next: () => this.tratarSucesso(false),
        error: (err) => this.tratarErro(err)
      });
  }

  private tratarSucesso(porPlanilha: boolean): void {
    this.isLoading = false;
    this.poDialogService.alert({
      title: 'Relatório - Balancete - Conferencia Saldo',
      message: porPlanilha
        ? 'Planilha enviada com sucesso. Dentro de instantes você receberá um e-mail com o balancete do grupo de empresas.'
        : 'Relatório solicitado com sucesso. Dentro de instantes você receberá um e-mail com o relatório.'
    });
    this.empresaId = null;
    this.periodo = {};
    this.limparArquivo();
  }

  private tratarErro(err: unknown): void {
    this.isLoading = false;
    const erro = err as { error?: { error?: string; message?: string }; message?: string };
    const mensagem =
      erro?.error?.error ||
      erro?.error?.message ||
      erro?.message ||
      'Erro ao solicitar o relatório de Balancete.';
    this.poNotificationService.error(mensagem);
  }
}
